"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.formMessageToText = void 0;
const domain_1 = require("@home-health-notify/domain");
const dayjs_1 = __importDefault(require("dayjs"));
const Date_1 = require("./Date");
const FormHelper_1 = require("./FormHelper");
const NA = "N/A";
const META_KEYS = ["templateType", "filledByAdmin", "offSet", "filledAs"];
const startCase = (value) => value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());
// "#/properties/a/properties/b" -> ["a", "b"]
const scopeSegments = (scope) => (scope !== null && scope !== void 0 ? scope : "")
    .split("/")
    .filter(segment => segment && segment !== "#" && segment !== "properties");
const resolveSchema = (rootSchema, scope) => {
    var _a;
    if (!scope)
        return undefined;
    let current = rootSchema;
    for (const segment of scopeSegments(scope)) {
        current = (_a = current === null || current === void 0 ? void 0 : current.properties) === null || _a === void 0 ? void 0 : _a[segment];
        if (!current)
            return undefined;
    }
    return current;
};
const resolveData = (rootData, scope) => {
    if (!scope)
        return rootData;
    let current = rootData;
    for (const segment of scopeSegments(scope)) {
        current = current === null || current === void 0 ? void 0 : current[segment];
        if (current === undefined || current === null)
            return current;
    }
    return current;
};
// Mirrors JsonForms label resolution: uischema label > schema title > startCase(scope).
const deriveLabel = (element, schema) => {
    const { label } = element !== null && element !== void 0 ? element : {};
    if (typeof label === "string")
        return label;
    if (label && typeof label === "object" && typeof label.text === "string") {
        return label.text;
    }
    if (typeof (schema === null || schema === void 0 ? void 0 : schema.title) === "string")
        return schema.title;
    const segments = scopeSegments(element === null || element === void 0 ? void 0 : element.scope);
    return segments.length ? startCase(segments[segments.length - 1]) : "";
};
const isLabelShown = (element) => {
    const { label } = element !== null && element !== void 0 ? element : {};
    if (typeof label === "boolean")
        return label;
    if (label && typeof label === "object" && typeof label.show === "boolean") {
        return label.show;
    }
    return true;
};
const formatDate = (value) => value ? (0, dayjs_1.default)(value).format(domain_1.HHNConstants.DATE_VIEW_FORMAT) : NA;
const formatTimeRange = (start, end) => {
    const startTime = start && (0, dayjs_1.default)(start).format(domain_1.HHNConstants.TIME_VIEW_FORMAT);
    const endTime = end && (0, dayjs_1.default)(end).format(domain_1.HHNConstants.TIME_VIEW_FORMAT);
    return startTime && endTime ? `${startTime} to ${endTime}` : NA;
};
// Mirrors OneOfRadioGroupControl's "Add" -> "Added", "Delete" -> "Deleted" mapping.
const oneOfRadioDisplay = (title) => {
    if (!title)
        return NA;
    if (title === "Add")
        return "Added";
    if (title === "Delete")
        return "Deleted";
    return title;
};
const pushLine = (lines, label, value, showLabel = true) => {
    lines.push(showLabel && label ? `${label}: ${value}` : value);
};
const renderElement = (element, rootSchema, rootData, lines) => {
    var _a, _b, _c, _d, _e, _f, _g;
    if (!element)
        return;
    const { type, scope } = element;
    const scopeStr = scope !== null && scope !== void 0 ? scope : "";
    // Group (rank 25): renders the services map, otherwise recurse its elements.
    if (type === "Group") {
        const services = rootData === null || rootData === void 0 ? void 0 : rootData.services;
        if (services && typeof services === "object") {
            Object.keys(services).forEach(service => pushLine(lines, service, `${services[service]}`));
            return;
        }
    }
    // StaticArrayLayout (rank 5): one line per array item.
    if (type === "StaticArrayLayout") {
        const arraySchema = resolveSchema(rootSchema, scope);
        const arrayData = resolveData(rootData, scope);
        const detailElements = (_c = (_b = (_a = element.options) === null || _a === void 0 ? void 0 : _a.detail) === null || _b === void 0 ? void 0 : _b.elements) !== null && _c !== void 0 ? _c : [];
        (Array.isArray(arrayData) ? arrayData : []).forEach((item) => renderArrayItem(detailElements, arraySchema === null || arraySchema === void 0 ? void 0 : arraySchema.items, item, lines));
        return;
    }
    // Layouts (VerticalLayout / HorizontalLayout / Group without services).
    if (Array.isArray(element.elements)) {
        element.elements.forEach((child) => renderElement(child, rootSchema, rootData, lines));
        return;
    }
    const schema = resolveSchema(rootSchema, scope);
    const data = resolveData(rootData, scope);
    // isOverride (rank 3) renders nothing.
    if (scopeStr.endsWith("isOverride"))
        return;
    // oneOf enum controls (rank 22).
    if (Array.isArray(schema === null || schema === void 0 ? void 0 : schema.oneOf)) {
        const options = (_d = element.options) !== null && _d !== void 0 ? _d : {};
        if (options.hideInMessage || (options.hideIfUndefined && data === undefined))
            return;
        const selected = schema.oneOf.find((option) => option.const === data);
        const title = selected === null || selected === void 0 ? void 0 : selected.title;
        if (options.oneOfStyle || options.format === "dropdown") {
            pushLine(lines, deriveLabel(element, schema), title !== null && title !== void 0 ? title : "", !!element.label);
        }
        else {
            pushLine(lines, deriveLabel(element, schema), oneOfRadioDisplay(title), !!element.label);
        }
        return;
    }
    // notes (rank 7): only rendered when there is content.
    if (scopeStr.endsWith("notes")) {
        if (data)
            pushLine(lines, deriveLabel(element, schema), `${data}`, isLabelShown(element));
        return;
    }
    // date control (rank 7).
    if (type === "Control" && (schema === null || schema === void 0 ? void 0 : schema.format) === "date") {
        pushLine(lines, deriveLabel(element, schema), formatDate(data), isLabelShown(element));
        return;
    }
    // phone number string control, maxLength 10 (rank 6).
    if ((schema === null || schema === void 0 ? void 0 : schema.type) === "string" && (schema === null || schema === void 0 ? void 0 : schema.maxLength) === 10) {
        const value = data
            ? (0, Date_1.formatDisplayPhoneNumber)(domain_1.CountryCode.US, data.toString()) || NA
            : NA;
        pushLine(lines, deriveLabel(element, schema), value, isLabelShown(element));
        return;
    }
    // AppointmentPicker (rank 5): a date line and a time-range line.
    if (type === "AppointmentPicker") {
        const label = deriveLabel(element, schema);
        pushLine(lines, `${label} Date`, formatDate(data === null || data === void 0 ? void 0 : data.visitDate));
        pushLine(lines, `${label} Time`, formatTimeRange(data === null || data === void 0 ? void 0 : data.visitStartTime, data === null || data === void 0 ? void 0 : data.visitEndTime));
        return;
    }
    // VisitPicker (rank 5).
    if (type === "VisitPicker") {
        pushLine(lines, "Visit Time", formatTimeRange(data === null || data === void 0 ? void 0 : data.visitStartTime, data === null || data === void 0 ? void 0 : data.visitEndTime));
        return;
    }
    // phoneNumberWithCountryCode object (rank 5).
    if (scopeStr.endsWith("phoneNumberWithCountryCode")) {
        const value = (data === null || data === void 0 ? void 0 : data.countryCode) && (data === null || data === void 0 ? void 0 : data.phoneNumber)
            ? (0, Date_1.formatDisplayPhoneNumber)(data.countryCode, data.phoneNumber.toString())
            : NA;
        pushLine(lines, "Phone Number", value);
        return;
    }
    // string control (rank 5).
    if (type === "Control" && (schema === null || schema === void 0 ? void 0 : schema.type) === "string") {
        if (((_e = element.options) === null || _e === void 0 ? void 0 : _e.hideIfUndefined) && data === undefined)
            return;
        pushLine(lines, deriveLabel(element, schema), `${data !== null && data !== void 0 ? data : ""}`, isLabelShown(element));
        return;
    }
    // DataLabel (rank 5): the value acts as the row label.
    if (type === "DataLabel" && (schema === null || schema === void 0 ? void 0 : schema.type) === "string") {
        lines.push(`${data !== null && data !== void 0 ? data : ""}`);
        return;
    }
    // number control with dynamic unit (rank 5).
    if (type === "Control" && (schema === null || schema === void 0 ? void 0 : schema.type) === "number") {
        const { unit, unitKey } = (_f = element.options) !== null && _f !== void 0 ? _f : {};
        const unitValue = unit ? `${unit} ${rootData === null || rootData === void 0 ? void 0 : rootData[unitKey]}` : rootData === null || rootData === void 0 ? void 0 : rootData[unitKey];
        const value = data ? `${data} ${unitValue}` : NA;
        pushLine(lines, deriveLabel(element, schema), value, isLabelShown(element));
        return;
    }
    // InputWithAdornment (rank 5).
    if (type === "InputWithAdornment") {
        const { suffix } = (_g = element.options) !== null && _g !== void 0 ? _g : {};
        const value = data ? (suffix ? `${data}${suffix}` : `${data}`) : NA;
        pushLine(lines, deriveLabel(element, schema), value, isLabelShown(element));
        return;
    }
    // boolean controls (rank 1) and anything unmatched render nothing.
};
// An array item renders its DataLabel as the row label and the remaining
// controls (radio status etc.) as the row value: e.g. "SN: Active".
const renderArrayItem = (detailElements, itemSchema, itemData, lines) => {
    const itemLines = [];
    let labelPrefix;
    detailElements.forEach((element) => {
        var _a;
        if ((element === null || element === void 0 ? void 0 : element.type) === "DataLabel") {
            const schema = resolveSchema(itemSchema, element.scope);
            if ((schema === null || schema === void 0 ? void 0 : schema.type) === "string") {
                labelPrefix = `${(_a = resolveData(itemData, element.scope)) !== null && _a !== void 0 ? _a : ""}`;
            }
            return;
        }
        renderElement(element, itemSchema, itemData, itemLines);
    });
    if (labelPrefix !== undefined) {
        lines.push(itemLines.length ? `${labelPrefix}: ${itemLines.join(", ")}` : labelPrefix);
    }
    else {
        lines.push(...itemLines);
    }
};
const fallbackLines = (messageData) => {
    const { form } = messageData;
    if (!form || typeof form !== "object")
        return [];
    return Object.entries(form)
        .filter(([key]) => !META_KEYS.includes(key))
        .map(([key, value]) => {
        const printable = value && typeof value === "object"
            ? JSON.stringify(value)
            : `${value}`;
        return `${startCase(key)}: ${printable}`;
    });
};
/**
 * Serializes a FORM message into readable "Label: Value" plain text that mirrors
 * the rendered form. Falls back to the raw form payload when the template is
 * unavailable so copy never fails. Always begins with the form title (body).
 */
const formMessageToText = (messageData, template) => {
    var _a, _b, _c;
    const title = (_a = messageData.body) !== null && _a !== void 0 ? _a : "";
    const lines = [];
    if ((template === null || template === void 0 ? void 0 : template.payloadToFormDataHandler) && template.uiSchema && template.schema) {
        try {
            const data = template.payloadToFormDataHandler(messageData, template);
            const reordered = (0, FormHelper_1.reorderUiSchema)({ label: "", form: template });
            const elements = (_c = (_b = reordered === null || reordered === void 0 ? void 0 : reordered.elements) !== null && _b !== void 0 ? _b : template.uiSchema.elements) !== null && _c !== void 0 ? _c : [];
            elements.forEach((element) => renderElement(element, template.schema, data, lines));
        }
        catch (_d) {
            lines.length = 0;
            lines.push(...fallbackLines(messageData));
        }
    }
    else {
        lines.push(...fallbackLines(messageData));
    }
    return [title, ...lines].filter(line => line !== undefined && line !== "").join("\n");
};
exports.formMessageToText = formMessageToText;
